import type { PlaceDisplayInput } from './placeDomain';

export interface PlaceLocationValue {
  location_label: string | null;
  place_id: string | null;
  formatted_address: string | null;
  latitude: number | null;
  longitude: number | null;
  city: string | null;
  state: string | null;
  /** Set when user removes a previously validated pin. */
  place_cleared?: boolean;
}

export const emptyPlaceLocationValue = (): PlaceLocationValue => ({
  location_label: null,
  place_id: null,
  formatted_address: null,
  latitude: null,
  longitude: null,
  city: null,
  state: null,
});

export function placeLocationFromEvent(event: {
  location?: string | null;
  place_id?: string | null;
  formatted_address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  city?: string | null;
  state?: string | null;
}): PlaceLocationValue {
  return {
    location_label: event.location ?? null,
    place_id: event.place_id ?? null,
    formatted_address: event.formatted_address ?? null,
    latitude: event.latitude ?? null,
    longitude: event.longitude ?? null,
    city: event.city ?? null,
    state: event.state ?? null,
  };
}

export function placeLocationFromShop(shop: {
  place_id?: string | null;
  formatted_address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  city?: string | null;
  state?: string | null;
}): PlaceLocationValue {
  return {
    location_label: null,
    place_id: shop.place_id ?? null,
    formatted_address: shop.formatted_address ?? null,
    latitude: shop.latitude ?? null,
    longitude: shop.longitude ?? null,
    city: shop.city ?? null,
    state: shop.state ?? null,
  };
}

export function placeLocationToEventPayload(value: PlaceLocationValue): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    location: value.location_label?.trim() || null,
    city: value.city?.trim() || null,
    state: value.state?.trim() || null,
  };
  if (value.place_cleared) {
    payload.clear_place = true;
    return payload;
  }
  if (value.place_id && value.latitude != null && value.longitude != null) {
    payload.place_id = value.place_id;
    payload.formatted_address = value.formatted_address;
    payload.latitude = value.latitude;
    payload.longitude = value.longitude;
  }
  return payload;
}

export function placeLocationToShopPayload(value: PlaceLocationValue): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    city: value.city?.trim() || null,
    state: value.state?.trim() || null,
  };
  if (value.place_cleared) {
    payload.clear_place = true;
    return payload;
  }
  if (value.place_id && value.latitude != null && value.longitude != null) {
    payload.place_id = value.place_id;
    payload.formatted_address = value.formatted_address;
    payload.latitude = value.latitude;
    payload.longitude = value.longitude;
  }
  return payload;
}

export function placeLocationToDisplay(value: PlaceLocationValue): PlaceDisplayInput {
  return {
    location_label: value.location_label,
    place_id: value.place_id,
    formatted_address: value.formatted_address,
    latitude: value.latitude,
    longitude: value.longitude,
    city: value.city,
    state: value.state,
  };
}

export function hasPlacePin(value: PlaceLocationValue): boolean {
  return Boolean(value.place_id && value.latitude != null && value.longitude != null);
}
